/*
 * Developed for G.J. Gardner Homes by Softeq Development Corporation
 * http://www.softeq.com
 */

import { Overlay } from '@angular/cdk/overlay';
import { Component, OnInit, ViewChild } from '@angular/core';
import { FormBuilder, FormGroup } from '@angular/forms';
import { ActivatedRoute } from '@angular/router';

import { BuilderUserContext } from '@gh/builder-shared';
import { SystemRoleBit, TodoStatus, UserAutocompleteDto } from '@gh/builder-shared-data';
import { getRouteInitData } from '@gh/common-shared';
import { ID } from '@gh/common-shared-data';
import { createEmptySlicedDataSource, SlicedDataSource } from '@gh/core-data';
import {
  AutocompleteLoadMoreEvent,
  DialogService,
  getControlValue,
  NotificationService,
  WaitingService,
  yesAnswer,
  yesNoConfirmation,
} from '@gh/core-ui';
import {
  equalsByKey,
  GRID_EVENT,
  GridComponent,
  LAYOUT_TODO_INBOX_DONE,
  LAYOUT_TODO_PASSED,
  LayoutBasedGridDefinition,
} from '@gh/core-ui-grid';
import { isNotNil } from '@gh/core-util';
import { filter$, map$, switchMap$ } from '@gh/rx/operators';

import { assign, eq, find, isEmpty, some, values } from 'lodash';
import { Observable, Subscription } from 'rxjs';

import {
  chooseDefinitionByType,
  SHOW_TYPE_DONE,
  SHOW_TYPE_INBOX,
  SHOW_TYPE_PASSED,
} from '../../builder-todo.utils';
import {
  TodoInputDialogConfig,
  TodoListBusinessService,
  TodoChangeStatusResponse,
  TodoDetails,
  TodoList,
} from '../../resources';
import { mutateTodoDialog } from '../dialog/mutate-todo-dialog/mutate-todo-dialog.route';
import { PassTodoConfig } from '../dialog';
import { passTodoDialog } from '../dialog/pass-todo-dialog/pass-todo-dialog.route';
import { TodoTypeMenuComponent } from '../type-menu/todo-type-menu.component';
import { TodoInboxDoneGridDefinition } from './grid-definitions/todo-inbox-done-grid-definition.service';
import { TodoPassedGridDefinition } from './grid-definitions/todo-passed-grid-definition.service';
import { MatCheckboxChange } from '@angular/material/checkbox';
import { MatSlideToggleChange } from '@angular/material/slide-toggle';

const multipleConfirmation = yesNoConfirmation('msg_todo_process_all_selected');
type ShowTypeMenuItem = {
  title: string;
  iconName: string;
  type: TodoStatus;
};
type ShowTypeMenu = ShowTypeMenuItem[];

@Component({
  standalone: false,
  moduleId: module.id ? String(module.id) : undefined,
  selector: 'gh-todo-list',
  templateUrl: 'todo-list.component.html',
  styleUrls: ['todo-list.component.scss'],
  viewProviders: [
    TodoInboxDoneGridDefinition,
    TodoPassedGridDefinition,
  ],
})
export class TodoListComponent implements OnInit {
  dataSource: SlicedDataSource<TodoList>;
  showTypeMenuItems: ShowTypeMenu = [{
    title: 'lbl_to_do_inbox',
    iconName: 'inbox-empty',
    type: SHOW_TYPE_INBOX,
  }, {
    title: 'lbl_to_do_done',
    iconName: 'done',
    type: SHOW_TYPE_DONE,
  }, {
    title: 'lbl_to_do_passed',
    iconName: 'people',
    type: SHOW_TYPE_PASSED,
  }];

  @ViewChild(GridComponent, { static: true }) grid: GridComponent;
  @ViewChild(TodoTypeMenuComponent, { static: true }) showTypeMenu: TodoTypeMenuComponent;

  isUserSelectionVisible: boolean = false;
  showType: TodoStatus = SHOW_TYPE_INBOX;
  displaySuppressWarnings: boolean = false;
  showFutureDates: boolean = false;
  gridDefinition: LayoutBasedGridDefinition;
  isSelectAllDisabled: boolean = false;

  autocompleteForm: FormGroup;
  isUserPrePopulated: boolean = false;
  suppressWarnings: boolean = false;
  showInactiveUsers: boolean = false;

  constructor(private route: ActivatedRoute,
              private waitings: WaitingService,
              private todoListBusiness: TodoListBusinessService,
              private notificationService: NotificationService,
              private userContext: BuilderUserContext,
              private builderUserContext: BuilderUserContext,
              private formBuilder: FormBuilder,
              private inboxDoneGridDefinition: TodoInboxDoneGridDefinition,
              private passedGridDefinition: TodoPassedGridDefinition,
              private overlay: Overlay,
              private dialogService: DialogService) {
    this.successMultipleAction = this.successMultipleAction.bind(this);
    this.successSingleAction = this.successSingleAction.bind(this);
    this.changeListStatusAction = this.changeListStatusAction.bind(this);
    this.changeStatusAction = this.changeStatusAction.bind(this);
    this.onModelUpdated = this.onModelUpdated.bind(this);
  }

  get hasSelectedUser(): boolean {
    return isNotNil(getControlValue(this.autocompleteForm, 'user'));
  }

  get selectedPartial(): boolean {
    return this.grid.isSelectedPartial();
  }

  get notAllAreActivity(): boolean {
    return !!this.grid.selectedRows.find((item) => !item.activityTodo);
  }

  get selectedAll(): boolean {
    return this.grid.isSelectedAll();
  }

  get displayOptionBar(): boolean {
    return isEmpty(this.grid.selectedRows);
  }

  get inboxShowType(): boolean {
    return eq(this.showType, SHOW_TYPE_INBOX);
  }

  get doneShowType(): boolean {
    return eq(this.showType, SHOW_TYPE_DONE);
  }

  get passedShowType(): boolean {
    return eq(this.showType, SHOW_TYPE_PASSED);
  }

  get optionBarClasses(): object {
    return {
      'button-set': true,
      'show-on-hover': true,
      'hidden': !this.displayOptionBar,
      'two-items': this.doneShowType,
      'one-item': this.passedShowType,
    };
  }

  ngOnInit(): void {
    const { gridLayoutInboxDone, gridLayoutPassed } = getRouteInitData(this.route);
    this.inboxDoneGridDefinition.bind(gridLayoutInboxDone);
    this.passedGridDefinition.bind(gridLayoutPassed);

    const gridDefinitionMap = {
      [LAYOUT_TODO_INBOX_DONE]: this.inboxDoneGridDefinition,
      [LAYOUT_TODO_PASSED]: this.passedGridDefinition,
    };
    this.gridDefinition = gridDefinitionMap[LAYOUT_TODO_INBOX_DONE];

    // logic for display user select
    const userVisibleMap = {
      office: this.builderUserContext.officeView && this.builderUserContext.hasSystemRole(SystemRoleBit.Franchisee),
      master: this.builderUserContext.masterView && this.builderUserContext.isSwitched()
        && this.builderUserContext.hasSystemRole(SystemRoleBit.Corporate),
      officeSwitched: this.builderUserContext.officeView && this.builderUserContext.isSwitched(),
    };
    this.isUserSelectionVisible = some(values(userVisibleMap), Boolean);
    this.isUserPrePopulated = this.todoListBusiness.isUserPrepopulated();

    this.autocompleteForm = this.formBuilder.group({
      user: this.initialAutocompleteUser(),
    });

    this.grid.ready$$.subscribe(() => {
      this.grid.api.addEventListener(GRID_EVENT.rowClicked, (event: any) => {
        if (!event.event.shiftKey) {
          this.onEdit(event.data);
        }
      });

      this.showTypeMenu.changeShowType.subscribe((type: string) => {
        assign(this, { showType: type });
        this.gridDefinition = gridDefinitionMap[chooseDefinitionByType(type)];
        this.loadData();
      });
      this.loadData();
    });

    this.route.queryParams.pipe(
      map$((x: any) => x?.id),
      filter$(Boolean))
      .subscribe((id: ID) => this.openTodo(id));
  }

  onModelUpdated(): void {
    this.enableSelectAllControl();
    this.deselectAllTodoItems();
  }

  onBeforeModelUpdated(): void {
    this.enableSelectAllControl(false);
    this.deselectAllTodoItems();
  }

  loadData(): void {
    const { showType, showFutureDates, isUserSelectionVisible } = this;
    const { value: selectedUser } = this.autocompleteForm.controls['user'];
    const { data: currentUser } = this.userContext;

    const userId = !isUserSelectionVisible
      ? currentUser && currentUser.id
      : selectedUser ? selectedUser.userId : void 0;

    const filter = { userId, status: showType, showFutureDates };

    if (this.grid.initialized) {
      this.deselectAllTodoItems();
    }

    this.dataSource = userId ? this.todoListBusiness.findAllAsDataSource(filter) : createEmptySlicedDataSource();
  }

  initialAutocompleteUser(): Maybe<{ fullName: string; userId: ID }> {
    const { id, firstName, lastName } = this.userContext.data;
    const { isUserPrePopulated } = this;

    return isUserPrePopulated
      ? { fullName: `${firstName} ${lastName}`, userId: id }
      : void 0;
  }

  loadMoreUsers($event: AutocompleteLoadMoreEvent): void {
    const { query, load } = $event;
    const { showInactiveUsers } = this;
    const fullQuery = { ...query, filter: { name: query.filter, showInactive: showInactiveUsers } };
    load(this.todoListBusiness.getAutocompleteUser(fullQuery));
  }

  isRowSelected(todo: TodoList): boolean {
    const { selectionPrimaryKey } = this.grid;
    return !!find(this.grid.selectedRows, <PredicateFn<any>>equalsByKey(todo, selectionPrimaryKey));
  }

  onShowInactive($event: MatCheckboxChange): void {
    assign(this, { showInactiveUsers: $event.checked });
  }

  onSelect(todo: TodoList): void {
    this.grid.selectRow(todo);
  }

  onSelectAll(): void {
    const { selectedAll } = this;
    if (selectedAll) {
      this.deselectAllTodoItems();
    } else {
      this.selectAllTodoItems();
    }
    // invoke(this.grid, this.selectedAll ? 'deselectAllRows' : 'selectAllRows');
  }

  successMultipleAction(): void {
    this.deselectAllTodoItems();
    this.loadData();
  }

  successSingleAction(): void {
    this.deselectAllTodoItems();
    this.loadData();
  }

  /**
   * Invokes any action (like done, undone, delete) on single To-do Item
   * @param todo TodoList The list of Todos
   * @param actionFn Function Action behavior
   */
  todoAction(todo: TodoList, actionFn: (t: TodoList) => Observable<unknown>): void {
    actionFn(todo)
      .pipe(this.waitings.operator())
      .subscribe({ next: this.successSingleAction, error: this.notificationService.processAll });
  }

  /**
   * Invokes any action (like done, undone, delete) on multiple To-do Item
   * @param actionFn Function Action behavior
   */
  todoListAction(actionFn: (rows: TodoList[]) => Observable<unknown>): Subscription {
    const { selectedRows } = this.grid;
    const { suppressWarnings } = this;
    const listAction$ = !suppressWarnings && selectedRows.length > 1
      ? this.dialogService.confirmation(multipleConfirmation).pipe(
        filter$(yesAnswer),
        switchMap$(() => actionFn(selectedRows).pipe(this.waitings.operator())))
      : actionFn(selectedRows);

    return listAction$.subscribe({ next: this.successMultipleAction, error: this.notificationService.processAll });
  }

  changeListStatusAction(nextStatus: TodoStatus): (todoList: TodoList[]) => Observable<TodoChangeStatusResponse> {
    const { showType } = this;
    return (todoList: TodoList[]): Observable<TodoChangeStatusResponse> =>
      this.todoListBusiness.changeStatus({
                                           fromStatus: showType,
                                           toStatus: nextStatus,
                                           todoList: todoList.filter((item) => !item.activityTodo) });
  }

  changeStatusAction(nextStatus: TodoStatus): (todoList: TodoList) => Observable<TodoChangeStatusResponse> {
    const { showType } = this;
    return (todo: TodoList): Observable<TodoChangeStatusResponse> =>
      this.todoListBusiness.changeStatus({ fromStatus: showType, toStatus: nextStatus, todoList: [todo] });
  }

  onDelete(todo: TodoList): void {
    const remove = (requestTodo: TodoList) => this.todoListBusiness.remove([requestTodo]);
    this.todoAction(todo, remove);
  }

  onDone(todo: TodoList): void {
    this.todoAction(todo, this.changeStatusAction(TodoStatus.DONE));
  }

  onUndone(todo: TodoList): void {
    this.todoAction(todo, this.changeStatusAction(TodoStatus.INBOX));
  }

  onDeleteSelected(): void {
    const removeSelected = (selectedRows: TodoList[]) => this.todoListBusiness.remove(selectedRows.filter((item) => !item.activityTodo));
    this.todoListAction(removeSelected);
  }

  /**
   * onDoneSelected & onUndoneSelected are separate methods
   * to have separate control if we will handle different situations before/after updates
   */
  onDoneSelected(): void {
    this.todoListAction(this.changeListStatusAction(TodoStatus.DONE));
  }

  onUndoneSelected(): void {
    this.todoListAction(this.changeListStatusAction(TodoStatus.INBOX));
  }

  onCreate(): void {
    this.dialogService.openWithInput(mutateTodoDialog, <TodoInputDialogConfig>{ todoId: void 0 })
      .afterClosed()
      .subscribe((todo?: TodoDetails) => {
        if (todo) {
          this.loadData();
        }
      });
  }

  onEdit(todo: TodoList): void {
    this.openTodo(todo.id, todo.activityTodo);
  }

  onPass(todo: TodoList): void {
    const config = <PassTodoConfig<TodoList>>{ selectedUserId: this.getSelectedUserId(), todo};
    this.dialogService.openWithInput(passTodoDialog, config, {autoFocus: false})
      .afterClosed()
      .subscribe((status: TodoChangeStatusResponse) => {
        if (status) {
          this.loadData();
        }
      });
  }

  onPassSelected(): void {
    const { selectedRows } = this.grid;
    const config = <PassTodoConfig<TodoList>>{ selectedUserId: this.getSelectedUserId(), todo: void 0, selectedRows};
    this.dialogService.openWithInput(passTodoDialog, config, {autoFocus: false})
      .afterClosed()
      .subscribe((status: TodoChangeStatusResponse) => {
        if (status) {
          this.loadData();
        }
      });
  }

  switchDisplayFutureDates($event: MatSlideToggleChange): void {
    const { checked } = $event;
    assign(this, { showFutureDates: checked });
    this.loadData();
  }

  switchSuppressWarnings($event: MatSlideToggleChange): void {
    const { checked } = $event;
    assign(this, { suppressWarnings: checked });
  }

  toggleSuppressWarning(): void {
    assign(this, { displaySuppressWarnings: !this.displaySuppressWarnings });
  }

  private getSelectedUserId(): ID {
    const { userId }: UserAutocompleteDto = getControlValue(this.autocompleteForm, 'user');
    return userId;
  }

  private openTodo(todoId: ID, activityTodo?: boolean): void {
    this.dialogService.openWithInput(mutateTodoDialog, <TodoInputDialogConfig>{ todoId, activityTodo })
      .afterClosed()
      .subscribe((responseTodo?: TodoDetails) => {
        if (responseTodo) {
          this.loadData();
        }
      });
  }

  /**
   * Changes disabled property of Select All control
   * @param isEnabled boolean Is control enabled
   */
  private enableSelectAllControl(isEnabled: boolean = true): void {
    setTimeout(() => {
      this.isSelectAllDisabled = !isEnabled;
    });
  }

  /**
   * Removes selection of all todo items from grid
   */
  private deselectAllTodoItems(): void {
    this.grid.deselectAllRows();
  }

  /**
   * Add selection of current page todo items to grid
   */
  private selectAllTodoItems(): void {
    this.grid.selectAllRows();
  }
}
